import React, { useMemo } from 'react';
import styled from 'styled-components';
import { MesaTable, MesaColumn } from '@mesa-labs/mesa-ui';

const StatCard = styled.div`
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  margin-bottom: 16px;

  /*
   * ClientPageContainer/OwnerPageContainer wraps this in a height-constrained
   * flex column (see App.tsx's MainContainer/#2537). flex-shrink defaults to 1,
   * and the flexbox "automatic minimum size" that normally floors a shrinking
   * item at its content height is suppressed for any overflow other than
   * visible — so without this, flexbox can crush this card's height toward 0
   * and the overflow:hidden above silently clips the whole table away.
   */
  flex-shrink: 0;
`;

const StatCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 18px;
  border-bottom: 1px solid #E5E7EB;
`;

const StatCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13.5px;
  font-weight: 700;
  color: #111827;
`;

const StatCardTitleIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 5px;
  background: #E0E7FF;
`;

const StatCardSubtitle = styled.span`
  font-size: 11.5px;
  color: #9CA3AF;
`;

export type MetricFormat = 'currency' | 'percentage' | 'days' | 'count' | 'number';

export interface MetricRow {
  label: string;
  t6m?: number | null;
  t12m?: number | null;
  allTime?: number | null;
  format?: MetricFormat;
}

export interface MetricGroup {
  label: string;
  rows: MetricRow[];
}

interface MetricStatTableProps {
  title: string;
  subtitle?: string;
  groups: MetricGroup[];
  /**
   * MesaTable localStorage persistence key for this instance's column
   * customizations. Required — this component is rendered multiple times
   * per page (see ExternalClientPageTemplate/ExternalVendorPageTemplate),
   * so callers must supply a value that's distinct across every call site
   * app-wide or their saved column settings will overwrite one another.
   */
  persistKey: string;
}

interface FlatMetricRow extends MetricRow {
  id: string;
  group: string;
}

function formatValue(value: number | null | undefined, format?: MetricFormat): string {
  if (value == null) return '—';
  const numericValue = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numericValue)) return '—';
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(numericValue);
    case 'percentage':
      return new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(numericValue);
    case 'days':
      return `${numericValue.toFixed(1)}d`;
    case 'count':
      return Math.round(numericValue).toLocaleString();
    default:
      return numericValue.toLocaleString();
  }
}

function buildColumns(hasAllTime: boolean): MesaColumn<FlatMetricRow>[] {
  const columns: MesaColumn<FlatMetricRow>[] = [
    {
      key: 'label',
      label: 'Metric',
      flex: true,
    },
    {
      key: 't6m',
      label: 'T6M',
      align: 'right',
      render: (value, row) => formatValue(value as number | null | undefined, row.format),
    },
    {
      key: 't12m',
      label: 'T12M',
      align: 'right',
      render: (value, row) => formatValue(value as number | null | undefined, row.format),
      style: { background: '#F5F3FF', color: '#4F46E5', fontWeight: 600 },
    },
  ];

  if (hasAllTime) {
    columns.push({
      key: 'allTime',
      label: 'All Time',
      align: 'right',
      render: (value, row) => formatValue(value as number | null | undefined, row.format),
    });
  }

  return columns;
}

function MetricStatTable({ title, subtitle, groups, persistKey }: MetricStatTableProps): React.ReactElement {
  const hasAllTime = useMemo(() => groups.some((g) => g.rows.some((r) => r.allTime != null)), [groups]);
  const columns = useMemo(() => buildColumns(hasAllTime), [hasAllTime]);
  const groupOrder = useMemo(() => groups.map((group) => group.label), [groups]);
  const rows = useMemo<FlatMetricRow[]>(() => groups.flatMap((group) => group.rows.map((row) => ({
    ...row,
    id: `${group.label}:${row.label}`,
    group: group.label,
  }))), [groups]);

  return (
    <StatCard>
      <StatCardHeader>
        <StatCardTitle>
          <StatCardTitleIcon />
          {title}
        </StatCardTitle>
        {subtitle && <StatCardSubtitle>{subtitle}</StatCardSubtitle>}
      </StatCardHeader>
      <MesaTable
        persistKey={persistKey}
        columns={columns}
        data={rows}
        getRowId={(row) => row.id}
        sorting={{ enabled: false }}
        grouping={{
          groupByKey: 'group',
          groupByOrder: groupOrder,
          getSectionHeader: (row) => row.group,
        }}
      />
    </StatCard>
  );
}

export default MetricStatTable;
